import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useFeather } from '../shell/useFeather';
import { LANGS, type LangCode } from './crm-lang';
import './crm-redesign.css';

interface FormDef {
  id: string;
  name: string;
  description: string;
  languages: LangCode[];
  embedPath: string;
}

const DEFAULT_FORMS_BY_SLUG: Record<string, FormDef[]> = {
  affilmaster: [
    {
      id: 'affiliate-registration',
      name: 'Affiliate registrace',
      description: 'Přihlašovací formulář pro nové affiliate partnery. Jazykové mutace CZ / SK / EN.',
      languages: ['cs', 'sk', 'en'],
      embedPath: '/api/v1/crm/public/embed',
    },
    {
      id: 'inzerent-registration',
      name: 'Poptávka Inzerent',
      description: 'Formulář pro inzerenty (firma, IČ, DIČ, web + GDPR souhlas). Jazykové mutace CZ / SK / EN.',
      languages: ['cs', 'sk', 'en'],
      embedPath: '/api/v1/crm/public/embed?form=inzerent',
    },
  ],
  affilplatform: [
    {
      id: 'demo-request',
      name: 'Registrace demo',
      description: 'Formulář pro B2B poptávku dema. Jazykové mutace CZ / SK / EN.',
      languages: ['cs', 'sk', 'en'],
      embedPath: '/api/v1/crm/public/embed',
    },
  ],
};

function formsForSlug(slug: string | null | undefined): FormDef[] {
  return DEFAULT_FORMS_BY_SLUG[slug ?? ''] ?? [];
}

function snippetFor(origin: string, embedPath: string, lang: LangCode | null): string {
  const sep = embedPath.includes('?') ? '&' : '?';
  const q = lang ? `${sep}lang=${lang}` : '';
  const url = `${origin}${embedPath}${q}`;
  return `<iframe src="${url}" width="100%" height="560" style="border:0;max-width:560px" title="Kontaktní formulář"></iframe>`;
}

export function CrmFormularPage({ hideHeading = false }: { hideHeading?: boolean }) {
  const { me } = useAuth();
  const forms = formsForSlug(me?.activeSpaceSlug);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = forms.find((f) => f.id === selectedId) ?? null;
  useFeather([selectedId, forms.length]);

  return <div className="crm-page crm-forms-page">
    {!selected
      ? <>{!hideHeading && <header className="crm-page-head"><span className="crm-eyebrow">CRM</span><h1>Formuláře</h1><p>Sběr nových poptávek pro web a kampaně.</p></header>}<FormList forms={forms} onOpen={setSelectedId} /></>
      : <FormDetail form={selected} onBack={() => setSelectedId(null)} />}
  </div>;
}

function FormList({ forms, onOpen }: { forms: FormDef[]; onOpen: (id: string) => void }) {
  return (
    <section className="crm-form-library">
      <div className="crm-form-library-intro"><span>Aktivní formuláře</span><strong>{forms.length}</strong><p>Každá odeslaná poptávka se automaticky objeví mezi leady.</p></div>
      <div className="crm-form-catalog">
        {forms.length === 0 ? <div className="crm-form-empty">Zatím žádný formulář.</div> : forms.map((f) => (
          <button key={f.id} type="button" className="crm-form-card" onClick={() => onOpen(f.id)}>
            <span className="crm-form-icon"><i data-feather="file-text" aria-hidden="true" /></span>
            <span className="crm-form-card-copy"><strong>{f.name}</strong><small>{f.description}</small></span>
            <span className="crm-form-langs">{f.languages.map((code) => { const l=LANGS.find((x)=>x.code===code); return l ? <i key={code}>{l.flag} {l.label}</i> : null; })}</span>
            <span className="crm-form-open">Otevřít</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function FormDetail({ form, onBack }: { form: FormDef; onBack: () => void }) {
  const origin = window.location.origin;
  const [previewLang, setPreviewLang] = useState<LangCode>(form.languages[0] ?? 'cs');
  const [copied, setCopied] = useState<LangCode | null>(null);

  function copy(lang: LangCode) {
    const text = snippetFor(origin, form.embedPath, lang);
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(lang);
        setTimeout(() => setCopied((c) => (c === lang ? null : c)), 1800);
      },
      () => undefined,
    );
  }

  const previewSep = form.embedPath.includes('?') ? '&' : '?';
  const previewUrl = `${origin}${form.embedPath}${previewSep}lang=${previewLang}`;
  const activeLangs = LANGS.filter((l) => form.languages.includes(l.code));

  return (
    <div className="crm-form-detail">
      <div className="crm-form-detail-back">
        <button type="button" onClick={onBack}>
          Zpět na seznam
        </button>
      </div>
      <header className="crm-form-detail-head"><span className="crm-eyebrow">Veřejný formulář</span><h1>{form.name}</h1><p>{form.description}</p></header>
      <div className="crm-form-workspace">
        <aside className="crm-form-setup">
              <span className="crm-form-step">01 · Nasazení</span>
              <h2>Vložení na web</h2>
              <p>Vyberte jazykovou mutaci a zkopírujte připravený kód.</p>
              {activeLangs.map((l) => {
                const code = snippetFor(origin, form.embedPath, l.code);
                return (
                  <div key={l.code} className="crm-form-code">
                    <label>
                      <span>{l.flag} <strong>{l.fullName}</strong></span>
                      <button
                        type="button"
                        className={copied === l.code ? 'copied' : ''}
                        onClick={() => copy(l.code)}
                      >
                        {copied === l.code ? 'Zkopírováno ✓' : 'Kopírovat'}
                      </button>
                    </label>
                    <textarea
                      rows={2}
                      readOnly
                      value={code}
                      onFocus={(e) => e.currentTarget.select()}
                    />
                  </div>
                );
              })}
              <div className="crm-form-tip">Jazyk mutace se uloží přímo k novému leadu.</div>
        </aside>
        <section className="crm-form-preview">
              <div className="crm-form-preview-head">
                <div><span className="crm-form-step">02 · Kontrola</span><h2>Živý náhled</h2></div>
                <div className="crm-form-lang-switch" role="group">
                  {activeLangs.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      className={previewLang === l.code ? 'active' : ''}
                      onClick={() => setPreviewLang(l.code)}
                    >
                      {l.flag} {l.label}
                    </button>
                  ))}
                </div>
              </div>
              <iframe
                key={previewLang}
                src={previewUrl}
                width="100%"
                height="560"
                style={{ border: 0, maxWidth: 560 }}
                title="Náhled formuláře"
              />
        </section>
      </div>
    </div>
  );
}
